import {
  AppLanguages,
  AssetType,
  BrokerTierEnum,
  CurrencyCode,
  Money,
  PaymentMethodTypeEnum,
  TransactionType,
} from 'interfaces'
import { AppFeaturesConfig } from '../features'
import {
  UNICREDIT_TRADING_VIEW_COLORS,
  UNICREDIT_TRADING_VIEW_SIMPLE_CHART_COLORS,
} from './tradingViewConstants'

const transactionMinimumCost: Money = {
  amount: 2,
  currency: 'EUR',
}

const config: AppFeaturesConfig = {
  info: {
    longName: 'Unicredit',
    shortName: 'Unicredit',
  },
  resources: {
    favicon: '/clients/unicredit-pl/assets/favicon.ico',
    logo: '/clients/unicredit-pl/assets/logo.svg',
    navbarLogo: '/clients/unicredit-pl/assets/logo.svg',
    loginLogo: '/clients/unicredit-pl/assets/login-logo.svg',
    qrCode: '/clients/unicredit-pl/assets/svg/webapp-stores.svg',
    pushNotificationLogo:
      '/clients/unicredit-pl/assets/svg/push-notification-logo.svg',
  },
  styles: [
    `/clients/unicredit-pl/themes/colors.css`,
    `/clients/unicredit-pl/themes/light.css`,
    `/clients/unicredit-pl/themes/dark.css`,
    `/clients/unicredit-pl/themes/typography.css`,
    `/clients/unicredit-pl/themes/variables.css`,
  ],
  tradingView: {
    advancedChartColorsConfig: UNICREDIT_TRADING_VIEW_COLORS,
    simpleChartColorsConfig: UNICREDIT_TRADING_VIEW_SIMPLE_CHART_COLORS,
    customCss: `../clients/unicredit-pl/themes/tv_custom.css`,
  },
  screens: {
    companyDetail: {
      showAnalystRatings: true,
      showEarnings: true,
      showBullBearsSay: true,
      showNews: true,
    },
    profile: {
      screens: {
        help: null,
      },
    },
  },
  i18n: {
    languages: [AppLanguages.en, AppLanguages.pl],
    resourcesUrls: {
      [AppLanguages.en]: '/clients/unicredit-pl/i18n/en/translation.json',
      [AppLanguages.pl]: '/clients/unicredit-pl/i18n/pl/translation.json',
    },
  },
  features: {
    allowStockDistributionChart: false,
    allowExtendedHoursTrading: false,
    allowSignUp: false,
    allowFinny: false,
    assetsAllowed: [AssetType.stock, AssetType.etf],
    transactionsAllowed: [
      TransactionType.sell,
      TransactionType.buy,
      TransactionType.dividendCash,
      TransactionType.dividendShares,
      TransactionType.deposit,
      TransactionType.noteCredit,
      TransactionType.noteDebit,
      TransactionType.withdrawal,
      TransactionType.safekeeping,
      TransactionType.walletTransferIncoming,
      TransactionType.walletTransferOutging,
    ],
    paymentMethods: {
      topup: [
        {
          type: PaymentMethodTypeEnum.bank,
          addPaymentMethod: true,
          allowAsDefault: true,
        },
        {
          type: PaymentMethodTypeEnum.card,
          addPaymentMethod: true,
          allowAsDefault: true,
        },
        {
          type: PaymentMethodTypeEnum.manualTransfer,
          addPaymentMethod: true,
          allowAsDefault: false,
        },
      ],
      cashout: [
        {
          type: PaymentMethodTypeEnum.bank,
          addPaymentMethod: true,
          allowAsDefault: true,
        },
        {
          type: PaymentMethodTypeEnum.manualTransfer,
          addPaymentMethod: true,
          allowAsDefault: true,
        },
      ],
    },
    orders: {
      allowLimitOrders: true,
      allowMarketOrders: true,
      limitOrdersWhenMarketIsClosed: true,
      allowStopLossAndTakeProfit: true,
    },
    recurringPayments: false,
    portfolioTransfer: false,
  },
  constants: {
    brokerTiers: {
      common: {
        transferByCardFee: 1,
        transferViaBankMinimumCost: {
          amount: 100,
          currency: 'EUR',
        },
        transferByCardMinimumCost: {
          amount: 100,
          currency: 'EUR',
        },
        transferByCardMaximumCost: {
          amount: 15000,
          currency: 'EUR',
        },
      },
      tiers: [
        {
          id: BrokerTierEnum.lite,
          transactionMinimumCost,
          currencyExchangeFee: 0.25,
          mutualFundsExtraInterest: 0.25,
          transactionPercentualCost: 0.1,
          minimumTradesPerMonth: 0,
          minimumGlobalPosition: {
            amount: 0,
            currency: 'EUR',
          },
        },
        {
          id: BrokerTierEnum.pro,
          transactionMinimumCost,
          currencyExchangeFee: 0.1,
          transactionPercentualCost: 0.05,
          mutualFundsExtraInterest: 0.5,
          minimumTradesPerMonth: 10,
          minimumGlobalPosition: {
            amount: 25000,
            currency: 'EUR',
          },
        },
      ],
    },
    faqsUrls: null,
    portfolioTransferInfoUrls: null,
    privacyPolicyUrls: null,
    securityUrls: null,
    supportEmail: 'support@heytrade.com',
    termsAndConditions: 'https://heytrade.com/terms-of-use',
    truelayer: {
      privacyPolicyUrl: {
        es: 'https://truelayer.com/es-es/legal/aviso-de-privacidad',
        en: 'https://truelayer.com/legal/privacy',
        ka: null,
        pl: null,
      },
      termsAndConditions: {
        es: 'https://truelayer.com/es-es/legal/condiciones-del-servicio-para-el-usuario-final',
        en: 'https://truelayer.com/legal/enduser_tos',
        ka: null,
        pl: null,
      },
    },
    defaultLang: 'en-US',
    defaultCurrency: CurrencyCode.PLN,
    minimumTopUpQuantityRequired: true,
  },
  infra: {
    sentryDSN:'https://1b0b77b360fc0350a250e1c1c14d08b9@o1010857.ingest.us.sentry.io/4509592670568448',
    localStorageKey: 'unicreditsettings',
  },
}

export default config
